import React, { useState } from 'react';
import { MapPin, Phone, Mail, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { companyInfo, submitContactForm, submitQuoteForm } from '../data/mock';
import { toast } from 'sonner';

const Contact = () => {
  const [activeForm, setActiveForm] = useState('contact'); // 'contact' or 'quote'
  const [contactLoading, setContactLoading] = useState(false);
  const [quoteLoading, setQuoteLoading] = useState(false);

  const [contactFormData, setContactFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [quoteFormData, setQuoteFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    serviceType: '',
    vesselType: '',
    location: '',
    urgency: '',
    details: ''
  });

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactLoading(true);
    
    try {
      const response = await submitContactForm(contactFormData);
      if (response.success) {
        toast.success(response.message);
        setContactFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        toast.error(response.message || 'Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setContactLoading(false);
    }
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setQuoteLoading(true);
    
    try {
      const response = await submitQuoteForm(quoteFormData);
      if (response.success) {
        toast.success(response.message);
        setQuoteFormData({
          name: '',
          company: '',
          email: '',
          phone: '',
          serviceType: '',
          vesselType: '',
          location: '',
          urgency: '',
          details: ''
        });
      } else {
        toast.error(response.message || 'Failed to send quote request. Please try again.');
      }
    } catch (error) {
      console.error('Quote form error:', error);
      toast.error('Failed to send quote request. Please try again.');
    } finally {
      setQuoteLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-maritime-primary">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1713859288954-3314e2fcaa7a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwzfHxjb250YWluZXIlMjBwb3J0JTIwdGVybWluYWx8ZW58MHx8fHwxNzcxMjAyMjIwfDA&ixlib=rb-4.1.0&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl font-bold text-white mb-6">Contact Us</h1>
            <p className="text-xl text-gray-200 leading-relaxed">
              Get in touch with our expert team for professional marine survey services 
              across Australia, New Zealand, Pacific Islands, and India.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 -mt-24 relative z-10">
            <Card className="border-2 border-maritime-accent/20 shadow-xl hover:shadow-2xl transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="w-14 h-14 bg-maritime-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-7 h-7 text-maritime-accent" />
                </div>
                <h3 className="font-semibold text-maritime-primary mb-2">Our Location</h3>
                <p className="text-gray-600 text-sm">{companyInfo.address}</p>
              </CardContent>
            </Card>

            <Card className="border-2 border-maritime-accent/20 shadow-xl hover:shadow-2xl transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="w-14 h-14 bg-maritime-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-7 h-7 text-maritime-accent" />
                </div>
                <h3 className="font-semibold text-maritime-primary mb-2">Call Us</h3>
                <a
                  href={`tel:${companyInfo.phone}`}
                  className="text-maritime-accent hover:text-maritime-accent/80 font-medium text-sm transition-colors"
                >
                  {companyInfo.phone}
                </a>
              </CardContent>
            </Card>

            <Card className="border-2 border-maritime-accent/20 shadow-xl hover:shadow-2xl transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="w-14 h-14 bg-maritime-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-7 h-7 text-maritime-accent" />
                </div>
                <h3 className="font-semibold text-maritime-primary mb-2">Email Us</h3>
                <a
                  href={`mailto:${companyInfo.emails.operations}`}
                  className="text-maritime-accent hover:text-maritime-accent/80 font-medium text-sm transition-colors"
                >
                  {companyInfo.emails.operations}
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Forms Section */}
      <section className="py-20 bg-gradient-to-b from-white to-maritime-primary/5">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            {/* Form Tabs */}
            <div className="flex justify-center gap-4 mb-8">
              <button
                onClick={() => setActiveForm('contact')}
                className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                  activeForm === 'contact'
                    ? 'bg-maritime-accent text-white shadow-lg'
                    : 'bg-white text-gray-600 hover:bg-gray-50 border-2 border-gray-200'
                }`}
              >
                Simple Contact
              </button>
              <button
                onClick={() => setActiveForm('quote')}
                className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                  activeForm === 'quote'
                    ? 'bg-maritime-accent text-white shadow-lg'
                    : 'bg-white text-gray-600 hover:bg-gray-50 border-2 border-gray-200'
                }`}
              >
                Request Quote
              </button>
            </div>

            {/* Simple Contact Form */}
            {activeForm === 'contact' && (
              <Card className="border-2 border-maritime-accent/20 shadow-xl">
                <CardContent className="p-8">
                  <div className="mb-6">
                    <h2 className="text-3xl font-bold text-maritime-primary mb-2">
                      Send Us a Message
                    </h2>
                    <p className="text-gray-600">
                      Have a question? Fill out the form below and we'll get back to you shortly.
                    </p>
                  </div>

                  <form onSubmit={handleContactSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="contact-name">Name *</Label>
                        <Input
                          id="contact-name"
                          type="text"
                          required
                          placeholder="Your full name"
                          value={contactFormData.name}
                          onChange={(e) =>
                            setContactFormData({ ...contactFormData, name: e.target.value })
                          }
                          className="mt-2"
                        />
                      </div>

                      <div>
                        <Label htmlFor="contact-email">Email *</Label>
                        <Input
                          id="contact-email"
                          type="email"
                          required
                          placeholder="your.email@example.com"
                          value={contactFormData.email}
                          onChange={(e) =>
                            setContactFormData({ ...contactFormData, email: e.target.value })
                          }
                          className="mt-2"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="contact-phone">Phone</Label>
                      <Input
                        id="contact-phone"
                        type="tel"
                        placeholder="+64 XX XXX XXXX"
                        value={contactFormData.phone}
                        onChange={(e) =>
                          setContactFormData({ ...contactFormData, phone: e.target.value })
                        }
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="contact-message">Message *</Label>
                      <Textarea
                        id="contact-message"
                        required
                        placeholder="How can we help you?"
                        rows={6}
                        value={contactFormData.message}
                        onChange={(e) =>
                          setContactFormData({ ...contactFormData, message: e.target.value })
                        }
                        className="mt-2"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={contactLoading}
                      className="w-full bg-maritime-accent hover:bg-maritime-accent/90 text-white"
                      size="lg"
                    >
                      {contactLoading ? (
                        'Sending...'
                      ) : (
                        <>
                          Send Message
                          <Send className="ml-2 w-5 h-5" />
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}

            {/* Quote Request Form */}
            {activeForm === 'quote' && (
              <Card className="border-2 border-maritime-accent/20 shadow-xl">
                <CardContent className="p-8">
                  <div className="mb-6">
                    <h2 className="text-3xl font-bold text-maritime-primary mb-2">
                      Request a Quote
                    </h2>
                    <p className="text-gray-600">
                      Provide details about your marine survey requirements for a detailed quote.
                    </p>
                  </div>

                  <form onSubmit={handleQuoteSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="quote-name">Name *</Label>
                        <Input
                          id="quote-name"
                          type="text"
                          required
                          placeholder="Your full name"
                          value={quoteFormData.name}
                          onChange={(e) =>
                            setQuoteFormData({ ...quoteFormData, name: e.target.value })
                          }
                          className="mt-2"
                        />
                      </div>

                      <div>
                        <Label htmlFor="quote-company">Company</Label>
                        <Input
                          id="quote-company"
                          type="text"
                          placeholder="Company name"
                          value={quoteFormData.company}
                          onChange={(e) =>
                            setQuoteFormData({ ...quoteFormData, company: e.target.value })
                          }
                          className="mt-2"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="quote-email">Email *</Label>
                        <Input
                          id="quote-email"
                          type="email"
                          required
                          placeholder="your.email@example.com"
                          value={quoteFormData.email}
                          onChange={(e) =>
                            setQuoteFormData({ ...quoteFormData, email: e.target.value })
                          }
                          className="mt-2"
                        />
                      </div>

                      <div>
                        <Label htmlFor="quote-phone">Phone *</Label>
                        <Input
                          id="quote-phone"
                          type="tel"
                          required
                          placeholder="+64 XX XXX XXXX"
                          value={quoteFormData.phone}
                          onChange={(e) =>
                            setQuoteFormData({ ...quoteFormData, phone: e.target.value })
                          }
                          className="mt-2"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="quote-service">Service Type *</Label>
                        <Select
                          value={quoteFormData.serviceType}
                          onValueChange={(value) =>
                            setQuoteFormData({ ...quoteFormData, serviceType: value })
                          }
                          required
                        >
                          <SelectTrigger className="mt-2">
                            <SelectValue placeholder="Select service" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="vessel-condition">Vessel Condition Survey</SelectItem>
                            <SelectItem value="lifting-gear">Lifting Gear Inspection</SelectItem>
                            <SelectItem value="cargo-inspection">Cargo Inspection</SelectItem>
                            <SelectItem value="on-off-hire">On/Off-Hire Inspection</SelectItem>
                            <SelectItem value="accident-investigation">Accident Investigation</SelectItem>
                            <SelectItem value="pre-purchase">Pre-Purchase Inspection</SelectItem>
                            <SelectItem value="bunker-survey">Bunker Survey</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label htmlFor="quote-vessel">Vessel Type</Label>
                        <Input
                          id="quote-vessel"
                          type="text"
                          placeholder="e.g., Cargo Ship, Tanker, etc."
                          value={quoteFormData.vesselType}
                          onChange={(e) =>
                            setQuoteFormData({ ...quoteFormData, vesselType: e.target.value })
                          }
                          className="mt-2"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="quote-location">Location *</Label>
                        <Input
                          id="quote-location"
                          type="text"
                          required
                          placeholder="Port or location"
                          value={quoteFormData.location}
                          onChange={(e) =>
                            setQuoteFormData({ ...quoteFormData, location: e.target.value })
                          }
                          className="mt-2"
                        />
                      </div>

                      <div>
                        <Label htmlFor="quote-urgency">Urgency *</Label>
                        <Select
                          value={quoteFormData.urgency}
                          onValueChange={(value) =>
                            setQuoteFormData({ ...quoteFormData, urgency: value })
                          }
                          required
                        >
                          <SelectTrigger className="mt-2">
                            <SelectValue placeholder="Select urgency" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="urgent">Urgent (24-48 hours)</SelectItem>
                            <SelectItem value="normal">Normal (3-5 days)</SelectItem>
                            <SelectItem value="flexible">Flexible (1-2 weeks)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="quote-details">Additional Details</Label>
                      <Textarea
                        id="quote-details"
                        placeholder="Please provide any additional information about your requirements..."
                        rows={6}
                        value={quoteFormData.details}
                        onChange={(e) =>
                          setQuoteFormData({ ...quoteFormData, details: e.target.value })
                        }
                        className="mt-2"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={quoteLoading}
                      className="w-full bg-maritime-accent hover:bg-maritime-accent/90 text-white"
                      size="lg"
                    >
                      {quoteLoading ? (
                        'Submitting...'
                      ) : (
                        <>
                          Submit Quote Request
                          <CheckCircle className="ml-2 w-5 h-5" />
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-16 bg-maritime-primary text-white">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <h2 className="text-3xl font-bold mb-4">Our Service Area</h2>
            <p className="text-xl text-gray-200 mb-6">
              Proudly serving maritime operations across {companyInfo.serviceArea}
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg px-6 py-3 border border-white/20">
                <span className="font-semibold">New Zealand</span>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg px-6 py-3 border border-white/20">
                <span className="font-semibold">Australia</span>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg px-6 py-3 border border-white/20">
                <span className="font-semibold">Pacific Islands</span>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg px-6 py-3 border border-white/20">
                <span className="font-semibold">India</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
